/**
 * Payload al motor, fuentes/joins, estilos y formato de métricas.
 */
import { auth } from '../../../firebaseConfig';
import { peekDriveAccessToken } from './googleDriveClient';
import { normalizeReportSections, withPageNavDefaults } from './pageNav';
import { normalizeSourceFilters, buildFixedEngineFilters } from './filterUtils';

export const PYTHON_ENGINE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';

/** fetch al backend /api/engine con Bearer Firebase. Nunca llama al motor Python en directo. */
export async function engineFetch(path, options = {}) {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('Inicia sesión para consultar el motor analítico.');
    }
    const token = await user.getIdToken();
    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {})
    };
    const googleToken = peekDriveAccessToken();
    if (googleToken) headers['X-Google-Access-Token'] = googleToken;
    const response = await fetch(`${PYTHON_ENGINE_URL}${path}`, { ...options, headers });
    return response;
}

let warmPingAt = 0;

/**
 * Despierta el motor (Cloud Run con min-instances=0) al entrar a la biblioteca:
 * la instancia y sus imports quedan listos antes de que el usuario abra un reporte.
 */
export function warmEngine() {
    const now = Date.now();
    if (now - warmPingAt < 60000) return;
    warmPingAt = now;
    engineFetch('/api/engine/health').catch(() => { warmPingAt = 0; });
}

/** Mismo fetch con Bearer para las rutas del backend que no son del motor. */
export const backendFetch = engineFetch;

/** Extrae detail/error del JSON de respuesta del motor o del proxy. */
export async function engineErrorMessage(response, fallback) {
    const payload = await response.clone().json().catch(() => ({}));
    if (typeof payload.detail === 'string') return payload.detail;
    if (Array.isArray(payload.detail)) {
        return payload.detail.map(item => item.msg || item.message || JSON.stringify(item)).join(' ');
    }
    return payload.error || payload.message || fallback;
}

const QUERY_BATCH_WINDOW_MS = 24;
const QUERY_TIMEOUT_MS = 120000;
const QUERY_CACHE_MAX = 60;
const QUERY_STORAGE_PREFIX = 'rq:';
const QUERY_STORAGE_MAX_CHARS = 250000;
// Tras un rato conviene volver a preguntar al motor aunque el payload sea el mismo.
const QUERY_STORAGE_TTL_MS = 15 * 60 * 1000;
const queryCache = new Map();
// Resultados de un snapshot reciente: hasta esta hora se pintan sin volver a preguntar
// al motor (el visor recalcula el snapshot cada 5 min). Clave de consulta → epoch ms.
const SNAPSHOT_TRUST_MS = 5 * 60 * 1000;
const trustedUntil = new Map();
let pendingBatch = null;

class QueryAbortError extends Error {
    constructor() {
        super('Consulta cancelada.');
        this.name = 'AbortError';
    }
}

function cacheKeyFor(payload) {
    return JSON.stringify(payload);
}

/** Hash corto (FNV-1a) para no guardar el payload entero como clave de sessionStorage. */
function storageKeyFor(key) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < key.length; i += 1) {
        hash ^= key.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return `${QUERY_STORAGE_PREFIX}${(hash >>> 0).toString(36)}`;
}

/** Map en memoria y, si no está, la copia de la pestaña (sobrevive a recargas). */
function readCached(key) {
    const hit = queryCache.get(key);
    if (hit) return hit;
    try {
        const raw = window.sessionStorage.getItem(storageKeyFor(key));
        if (!raw) return null;
        const stored = JSON.parse(raw);
        if (stored?.key !== key) return null;
        if (Date.now() - (stored.ts || 0) > QUERY_STORAGE_TTL_MS) {
            window.sessionStorage.removeItem(storageKeyFor(key));
            return null;
        }
        queryCache.set(key, stored.result);
        return stored.result;
    } catch (e) {
        return null;
    }
}

/** Resultado ya visto para ese payload exacto, o null. */
export function peekQueryResult(payload) {
    return readCached(cacheKeyFor(payload)) || null;
}

/**
 * Carga en la caché los resultados de un snapshot. Con `updatedAt` reciente (menos de
 * 5 min) los widgets los pintan sin consultar al motor; si es más viejo, se revalidan.
 */
export function primeQueryCache(pairs = [], updatedAt = null) {
    const builtAt = Date.parse(updatedAt || '');
    const until = Number.isFinite(builtAt)
        ? Math.min(builtAt, Date.now()) + SNAPSHOT_TRUST_MS
        : 0;
    (pairs || []).forEach((item) => {
        if (!item?.payload || !item?.result) return;
        const key = cacheKeyFor(item.payload);
        rememberQueryResult(key, item.result);
        if (until > Date.now()) trustedUntil.set(key, until);
        else trustedUntil.delete(key);
    });
}

/** True si el resultado en caché viene de un snapshot reciente y no hace falta revalidarlo. */
export function isQueryResultTrusted(payload) {
    const until = trustedUntil.get(cacheKeyFor(payload));
    return Boolean(until && until > Date.now());
}

function rememberQueryResult(key, result) {
    queryCache.set(key, result);
    while (queryCache.size > QUERY_CACHE_MAX) {
        queryCache.delete(queryCache.keys().next().value);
    }
    try {
        const raw = JSON.stringify({ key, result, ts: Date.now() });
        if (raw.length <= QUERY_STORAGE_MAX_CHARS) {
            window.sessionStorage.setItem(storageKeyFor(key), raw);
        }
    } catch (e) {
        // Sin espacio en sessionStorage la caché en memoria sigue sirviendo.
    }
}

// Versión de los datos del motor: sube tras «Actualizar» o el refresco automático
// y hace que cada widget montado vuelva a consultar sin desmontarse.
let engineDataVersion = 0;
const engineDataListeners = new Set();

export function currentEngineDataVersion() {
    return engineDataVersion;
}

/** Suscribe a los cambios de versión de datos. Devuelve la función para desuscribirse. */
export function subscribeEngineData(listener) {
    engineDataListeners.add(listener);
    return () => { engineDataListeners.delete(listener); };
}

/** Avisa a los widgets que los datos cambiaron (ya se limpió o se repobló la caché). */
export function bumpEngineDataVersion() {
    engineDataVersion += 1;
    engineDataListeners.forEach(listener => listener(engineDataVersion));
}

/**
 * Pide al motor releer de Drive las hojas de estas fuentes en la próxima consulta.
 * `maxAgeSeconds` respeta las copias más recientes (refresco automático compartido).
 */
export async function refreshEngineSources(dataSources = [], options = {}) {
    const sources = (dataSources || [])
        .filter(item => item && item.kind !== 'join' && item.spreadsheet_id)
        .map(item => ({ spreadsheet_id: item.spreadsheet_id }));
    if (!sources.length) return { success: true, invalidated: 0 };
    const response = await engineFetch('/api/engine/refresh', {
        method: 'POST',
        body: JSON.stringify({ data_sources: sources, max_age_seconds: options.maxAgeSeconds || 0 })
    });
    if (!response.ok) {
        throw new Error(await engineErrorMessage(response, 'No se pudieron actualizar las hojas de datos.'));
    }
    return response.json();
}

export function clearQueryCache() {
    queryCache.clear();
    trustedUntil.clear();
    try {
        Object.keys(window.sessionStorage)
            .filter(key => key.startsWith(QUERY_STORAGE_PREFIX))
            .forEach(key => window.sessionStorage.removeItem(key));
    } catch (e) { /* sessionStorage no disponible */ }
}

async function flushQueryBatch() {
    const batch = pendingBatch;
    pendingBatch = null;
    if (!batch || !batch.items.length) return;

    const live = batch.items.filter(item => !item.signal?.aborted);
    batch.items.filter(item => item.signal?.aborted).forEach(item => item.reject(new QueryAbortError()));
    if (!live.length) return;

    const fail = (message) => live.forEach(item => item.reject(new Error(message)));
    // Corta la espera si el motor no responde: mejor un error visible que girar sin fin.
    const guard = new AbortController();
    const timer = setTimeout(() => guard.abort(), QUERY_TIMEOUT_MS);
    try {
        const response = await engineFetch('/api/engine/query/batch', {
            method: 'POST',
            signal: guard.signal,
            body: JSON.stringify({
                queries: live.map((item, index) => ({ id: `w${index}`, ...item.payload }))
            })
        });
        if (!response.ok) {
            fail(await engineErrorMessage(response, 'Error al consultar el motor analítico.'));
            return;
        }
        const data = await response.json();
        const results = data.results || {};
        live.forEach((item, index) => {
            const result = results[`w${index}`];
            if (!result) {
                item.reject(new Error('El motor no devolvió datos para este elemento.'));
                return;
            }
            if (result.success === false) {
                item.reject(new Error(result.detail || 'La consulta no devolvió datos.'));
                return;
            }
            rememberQueryResult(item.key, result);
            item.resolve(result);
        });
    } catch (err) {
        fail(err.name === 'AbortError'
            ? 'El motor analítico no respondió a tiempo. Vuelve a intentarlo.'
            : (err.message || 'No se pudo contactar el motor analítico.'));
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Consulta del motor agrupando por lotes: los widgets que piden datos en el mismo
 * instante viajan en una sola petición, así el motor resuelve la fuente una vez.
 */
export function engineQuery(payload, options = {}) {
    const { signal, refresh = false } = options;
    const key = cacheKeyFor(payload);
    if (!refresh) {
        const cached = readCached(key);
        if (cached) return Promise.resolve(cached);
    }
    if (signal?.aborted) return Promise.reject(new QueryAbortError());

    return new Promise((resolve, reject) => {
        if (!pendingBatch) {
            pendingBatch = { items: [], timer: null };
            pendingBatch.timer = setTimeout(flushQueryBatch, QUERY_BATCH_WINDOW_MS);
        }
        pendingBatch.items.push({
            key,
            payload: refresh ? { ...payload, refresh: true } : payload,
            signal,
            resolve,
            reject
        });
    });
}

export function parseSpreadsheetInput(value) {
    const raw = String(value || '').trim();
    if (!raw) return { spreadsheet_id: '', gid: null };
    const idMatch = raw.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    const gidMatch = raw.match(/[#?&]gid=([0-9]+)/);
    return {
        spreadsheet_id: idMatch ? idMatch[1] : raw,
        gid: gidMatch ? Number(gidMatch[1]) : null
    };
}

export const CHART_PALETTE = ['#5E35B1', '#1a73e8', '#00897B', '#E91E63', '#FF8F00', '#3949AB', '#00ACC1', '#7CB342'];

export const WIDGET_TYPES = [
    { type: 'table', label: 'Tabla', w: 12, h: 6, minW: 1, minH: 2 },
    { type: 'bar', label: 'Barras', w: 6, h: 5, minW: 1, minH: 2 },
    { type: 'hbar', label: 'Barras horiz.', w: 6, h: 5, minW: 1, minH: 2 },
    { type: 'line', label: 'Líneas', w: 6, h: 5, minW: 1, minH: 2 },
    { type: 'area', label: 'Área', w: 6, h: 5, minW: 1, minH: 2 },
    { type: 'combo', label: 'Combinado', w: 6, h: 5, minW: 1, minH: 2 },
    { type: 'pie', label: 'Circular', w: 4, h: 5, minW: 1, minH: 2 },
    { type: 'map_mx', label: 'Mapa (México)', w: 6, h: 7, minW: 2, minH: 3 },
    { type: 'kpi', label: 'KPI', w: 3, h: 3, minW: 1, minH: 2 },
    { type: 'container', label: 'Bloque', w: 6, h: 8, minW: 3, minH: 5 },
    { type: 'slicer', label: 'Filtro', w: 4, h: 3, minW: 1, minH: 2 },
    { type: 'text', label: 'Texto', w: 4, h: 2, minW: 1, minH: 1 },
    { type: 'image', label: 'Imagen', w: 4, h: 4, minW: 1, minH: 1 },
    { type: 'divider', label: 'Divisor', w: 12, h: 1, minW: 1, minH: 1 }
];

export const DECORATION_TYPES = ['text', 'divider', 'image'];

export const CHART_WIDGET_TYPES = WIDGET_TYPES.filter(item =>
    ['table', 'bar', 'hbar', 'line', 'area', 'combo', 'pie', 'map_mx', 'kpi'].includes(item.type)
);
export const CONTROL_WIDGET_TYPES = WIDGET_TYPES.filter(item => item.type === 'slicer');
export const INSERT_WIDGET_TYPES = WIDGET_TYPES.filter(item =>
    ['text', 'divider', 'image', 'container'].includes(item.type)
);

export const SURFACE = {
    page: '#e8eaed',
    canvas: '#f4f5f7',
    card: '#ffffff',
    line: '#c4c7cc',
    lineStrong: '#9aa0a6',
    ink: '#202124',
    muted: '#3c4043',
    cardShadow: '0 1px 3px rgba(28, 29, 33, 0.12)',
};

export const DEFAULT_CANVAS = {
    widthMode: 'percent',
    width: 1200,
    height: 880,
    background: SURFACE.canvas,
    columns: 48,
    rowHeight: 20
};

export const CANVAS_GRID_COLUMNS = 48;
export const CANVAS_ROW_HEIGHT = 20;
export const LEGACY_CANVAS_COLUMNS = 12;
export const INNER_CANVAS_COLUMNS = 48;
export const CANVAS_RESIZE_HANDLES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
export const RGL_BREAKPOINTS = { lg: 0 };

export function rglCols(columns) {
    const n = Math.max(8, Number(columns) || CANVAS_GRID_COLUMNS);
    return { lg: n };
}

export function scaleLayout(layout = [], fromCols, toCols, yScale = 1) {
    const fx = (Number(toCols) || 1) / Math.max(1, Number(fromCols) || 1);
    const fy = Number(yScale) || 1;
    const maxW = Math.max(1, Number(toCols) || CANVAS_GRID_COLUMNS);
    return (layout || []).map(item => ({
        ...item,
        x: Math.round((Number(item.x) || 0) * fx),
        y: Math.round((Number(item.y) || 0) * fy),
        w: Math.max(1, Math.round((Number(item.w) || 1) * fx)),
        h: Math.max(1, Math.round((Number(item.h) || 1) * fy)),
        minW: 1,
        minH: 1,
        maxW
    }));
}

function inferLegacyCols(layout, fallback) {
    const maxX = (layout || []).reduce((acc, item) => Math.max(acc, (item.x || 0) + (item.w || 1)), 0);
    if (maxX <= 0) return fallback;
    if (maxX <= 8) return 8;
    if (maxX <= 12) return 12;
    return null;
}

function upgradeInnerWidgets(widgets, yScale) {
    return (widgets || []).map((widget) => {
        if (!widget?.children) return widget;
        const fromCols = inferLegacyCols(widget.children.layout, 8);
        const layout = fromCols
            ? scaleLayout(widget.children.layout || [], fromCols, CANVAS_GRID_COLUMNS, yScale)
            : (widget.children.layout || []).map(item => ({ ...item, maxW: CANVAS_GRID_COLUMNS, minW: 1, minH: 1 }));
        return {
            ...widget,
            children: {
                ...widget.children,
                layout,
                widgets: upgradeInnerWidgets(widget.children.widgets || [], yScale)
            }
        };
    });
}

export function upgradePageGrid(page = {}) {
    const original = page.canvas || {};
    const canvasIn = { ...DEFAULT_CANVAS, ...original };
    const storedCols = Number(original.columns);
    const layoutCols = inferLegacyCols(page.layout, null);
    const fromCols = Number.isFinite(storedCols) && storedCols > 0
        ? storedCols
        : (layoutCols || LEGACY_CANVAS_COLUMNS);
    if (fromCols >= 24) {
        return {
            ...page,
            canvas: { ...canvasIn, columns: fromCols },
            layout: (page.layout || []).map(item => ({ ...item, maxW: fromCols, minW: 1, minH: 1 })),
            widgets: page.widgets || []
        };
    }
    const fromH = Number(original.rowHeight) || 40;
    const toH = CANVAS_ROW_HEIGHT;
    const yScale = fromH / toH;
    return {
        ...page,
        canvas: { ...canvasIn, columns: CANVAS_GRID_COLUMNS, rowHeight: toH },
        layout: scaleLayout(page.layout || [], fromCols, CANVAS_GRID_COLUMNS, yScale),
        widgets: upgradeInnerWidgets(page.widgets || [], yScale)
    };
}

export function artboardSurface(canvas = DEFAULT_CANVAS, options = {}) {
    const showGrid = options.grid !== false;
    const step = Math.max(8, Number(canvas.rowHeight) || CANVAS_ROW_HEIGHT);
    const dots = 'radial-gradient(circle, rgba(60,64,67,0.28) 1px, transparent 1.15px)';
    const imageUrl = String(canvas.backgroundImage || '').trim();
    const fit = canvas.backgroundFit || 'cover';
    const size = fit === 'contain' ? 'contain' : (fit === 'repeat' ? 'auto' : 'cover');
    const color = canvas.background || DEFAULT_CANVAS.background;
    if (!imageUrl && !showGrid) {
        return {
            backgroundColor: color,
            backgroundImage: 'none',
            backgroundSize: 'auto',
            backgroundPosition: '0 0',
            backgroundRepeat: 'repeat',
        };
    }
    if (!imageUrl) {
        return {
            backgroundColor: color,
            backgroundImage: dots,
            backgroundSize: `${step}px ${step}px`,
            backgroundPosition: '12px 12px',
            backgroundRepeat: 'repeat',
        };
    }
    if (!showGrid) {
        return {
            backgroundColor: color,
            backgroundImage: `url(${JSON.stringify(imageUrl)})`,
            backgroundSize: size,
            backgroundPosition: 'center',
            backgroundRepeat: fit === 'repeat' ? 'repeat' : 'no-repeat',
        };
    }
    return {
        backgroundColor: color,
        backgroundImage: `${dots}, url(${JSON.stringify(imageUrl)})`,
        backgroundSize: `${step}px ${step}px, ${size}`,
        backgroundPosition: '12px 12px, center',
        backgroundRepeat: fit === 'repeat' ? 'repeat, repeat' : 'repeat, no-repeat',
    };
}

export function viewerArtboardStyle(canvas = DEFAULT_CANVAS) {
    return {
        ...artboardSurface(canvas, { grid: false }),
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: SURFACE.lineStrong,
        borderRadius: 12,
        boxShadow: '0 10px 28px rgba(28, 29, 33, 0.12)',
        boxSizing: 'border-box',
    };
}

/**
 * Marco de una tarjeta del dashboard. Con theme 'executive' el borde es más tenue
 * y la sombra más suave (tokens en dashboardTheme.js).
 */
export function widgetCardChrome(overrides = {}, theme = 'classic') {
    const executive = theme === 'executive';
    return {
        backgroundColor: SURFACE.card,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: executive ? '#e9ebef' : SURFACE.line,
        boxShadow: executive
            ? '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)'
            : SURFACE.cardShadow,
        boxSizing: 'border-box',
        ...overrides,
    };
}

export function isDecorationType(type) {
    return DECORATION_TYPES.includes(normalizeWidgetType(type));
}

export const NUMBER_FORMATS = [
    { value: 'default', label: 'Número (1,234.56)' },
    { value: 'decimal', label: 'Decimal (4 dígitos)' },
    { value: 'integer', label: 'Entero' },
    { value: 'percent', label: 'Porcentaje' },
    { value: 'currency_mxn', label: 'Moneda MXN' },
    { value: 'currency_usd', label: 'Moneda USD' },
    { value: 'compact', label: 'Compacto (1.2K)' }
];

export function createReportPage(name = 'Hoja 1', overrides = {}) {
    return {
        id: `page-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name,
        widgets: [],
        layout: [],
        canvas: { ...DEFAULT_CANVAS },
        icon: 'page',
        accent: '',
        hidden: false,
        sectionId: null,
        filters: [],
        ...overrides
    };
}

export function normalizeReportPages(raw = {}) {
    const sections = normalizeReportSections(raw.sections);
    const sectionIds = new Set(sections.map(section => section.id));
    if (Array.isArray(raw.pages) && raw.pages.length) {
        const pages = raw.pages.map((page, index) => withPageNavDefaults(upgradePageGrid({
            id: page.id || `page-${index + 1}`,
            name: String(page.name || `Hoja ${index + 1}`).trim() || `Hoja ${index + 1}`,
            widgets: page.widgets || [],
            layout: page.layout || [],
            canvas: page.canvas || raw.canvas || {},
            icon: page.icon,
            accent: page.accent,
            hidden: page.hidden,
            sectionId: page.sectionId && sectionIds.has(page.sectionId) ? page.sectionId : null,
            // Compatibilidad: reportes guardados antes de que los "Filtros del informe" fueran por
            // hoja tenían un único `raw.filters`; se replica a cada hoja hasta que el usuario edite.
            filters: page.filters || raw.filters || []
        })));
        const activePageId = pages.some(page => page.id === raw.activePageId)
            ? raw.activePageId
            : pages[0].id;
        return { pages, activePageId, sections };
    }

    const fallback = withPageNavDefaults(upgradePageGrid(createReportPage('Hoja 1', {
        id: 'page-default',
        widgets: raw.widgets || [],
        layout: raw.layout || [],
        canvas: raw.canvas || {},
        filters: raw.filters || []
    })));
    return { pages: [fallback], activePageId: fallback.id, sections };
}

export function firstPageDataSource(raw = {}) {
    const fromPages = (raw.pages || []).flatMap(page => page.widgets || []);
    const widgets = fromPages.length ? fromPages : (raw.widgets || []);
    return {
        spreadsheet_id: raw.dataSource?.spreadsheet_id || widgets[0]?.query?.spreadsheet_id || '',
        sheet_name: raw.dataSource?.sheet_name || widgets[0]?.query?.sheet_name || ''
    };
}

export const JOIN_TYPES = [
    { value: 'inner', label: 'Interna (inner)' },
    { value: 'left', label: 'Izquierda (left)' },
    { value: 'right', label: 'Derecha (right)' },
    { value: 'outer', label: 'Completa (full)' }
];

export function normalizeSelectedColumns(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const dimensions = (Array.isArray(raw.dimensions) ? raw.dimensions : [])
        .map(name => String(name || '').trim())
        .filter(Boolean);
    const metrics = (Array.isArray(raw.metrics) ? raw.metrics : [])
        .map(name => String(name || '').trim())
        .filter(Boolean);
    const order = (Array.isArray(raw.order) ? raw.order : [...dimensions, ...metrics])
        .map(name => String(name || '').trim())
        .filter((name, index, list) => name && list.indexOf(name) === index && (dimensions.includes(name) || metrics.includes(name)));
    const aliases = {};
    const rawAliases = raw.aliases && typeof raw.aliases === 'object' ? raw.aliases : {};
    Object.keys(rawAliases).forEach((key) => {
        const label = String(rawAliases[key] || '').trim();
        if (key && label) aliases[key] = label;
    });
    return {
        dimensions,
        metrics,
        order: order.length ? order : [...dimensions, ...metrics],
        aliases,
    };
}

export function selectedColumnOrder(source, columns = []) {
    const sel = source?.selectedColumns;
    if (!sel) return (columns || []).map(col => col.name);
    if ((sel.order || []).length) return sel.order.filter(name => (
        (sel.dimensions || []).includes(name) || (sel.metrics || []).includes(name)
    ));
    return [...(sel.dimensions || []), ...(sel.metrics || [])];
}

export function selectedColumnRole(source, colName, inferredType) {
    const sel = source?.selectedColumns;
    if (sel?.metrics?.includes(colName)) return 'metric';
    if (sel?.dimensions?.includes(colName)) return 'dimension';
    return inferredType === 'number' ? 'metric' : 'dimension';
}

export function selectedColumnAlias(source, colName) {
    const label = source?.selectedColumns?.aliases?.[colName];
    return (label && String(label).trim()) || colName;
}

export function createDataSource(partial = {}) {
    return {
        id: `ds-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        kind: 'sheet',
        label: partial.label || 'Fuente',
        spreadsheet_id: '',
        sheet_name: '',
        input: '',
        ...partial,
        filters: normalizeSourceFilters(partial.filters),
        selectedColumns: normalizeSelectedColumns(partial.selectedColumns),
    };
}

export function createJoin(partial = {}) {
    return {
        id: `join-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'left',
        leftDataSourceId: '',
        rightDataSourceId: '',
        keys: [{ left: '', right: '' }],
        ...partial
    };
}

export function serializeDataSources(dataSources = [], { includeSelection = true } = {}) {
    return dataSources.map(item => ({
        id: item.id,
        kind: item.kind || 'sheet',
        label: item.label,
        spreadsheet_id: item.spreadsheet_id || '',
        sheet_name: item.sheet_name || '',
        joinId: item.joinId,
        filters: buildFixedEngineFilters(item.filters || []),
        selectedColumns: includeSelection
            ? (normalizeSelectedColumns(item.selectedColumns) || undefined)
            : undefined,
        // Solo viaja si no es automático: así las consultas y snapshots existentes no cambian de clave.
        dateOrder: normalizeDateOrder(item.dateOrder) !== 'auto' ? normalizeDateOrder(item.dateOrder) : undefined,
    }));
}

export function serializeJoins(joins = []) {
    return joins.map(item => ({
        id: item.id,
        type: item.type || 'left',
        leftDataSourceId: item.leftDataSourceId,
        rightDataSourceId: item.rightDataSourceId,
        keys: (item.keys || []).map(key => ({ left: key.left || '', right: key.right || '' }))
    }));
}

export function serializeCalculatedFields(list = []) {
    return (Array.isArray(list) ? list : [])
        .filter(item => item && item.name && String(item.formula || '').trim())
        .map(item => ({ name: item.name, formula: item.formula }));
}

/** Campos spreadsheet/fuente/joins que viajan en cada query al motor. */
export function sourceRequestFields(sourceOrQuery = {}, catalog = {}, options = {}) {
    const dataSources = catalog.dataSources || catalog.data_sources || [];
    const joins = catalog.joins || [];
    const id = sourceOrQuery.dataSourceId || sourceOrQuery.data_source_id || sourceOrQuery.id;
    const ds = dataSources.find(item => item.id === id);
    const includeSelection = options.includeSelection !== false;
    return {
        spreadsheet_id: ds?.spreadsheet_id || sourceOrQuery.spreadsheet_id || '',
        sheet_name: ds?.sheet_name || sourceOrQuery.sheet_name || '',
        data_source_id: id || undefined,
        data_sources: dataSources.length ? serializeDataSources(dataSources, { includeSelection }) : undefined,
        joins: joins.length ? serializeJoins(joins) : undefined
    };
}

/** Un widget solo consulta al motor si tiene dimensiones, métricas o fórmulas. */
export function widgetHasQueryShape(query = {}) {
    return (query.dimensions || []).length > 0
        || Object.keys(query.metrics || {}).length > 0
        || (query.visibleMetrics || []).length > 0
        || (query.agg_calculated_fields || []).length > 0;
}

/** Agrupaciones posibles para una dimensión de fecha (motor: date_grains). */
export const DATE_GRAIN_OPTIONS = [
    { value: '', label: 'Sin agrupar (día exacto)' },
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'year', label: 'Año' }
];

/** Métrica interna con la meta del KPI cuando la meta viene de una columna. */
export const KPI_TARGET_KEY = '__kpi_target';

/**
 * Opciones de Top N que viajan al motor (orden + límite + «Otros»). Solo tablas y
 * gráficas con Top N > 0; así el corte se hace sobre el orden correcto y no sobre
 * las primeras filas que devuelva el agrupado.
 */
function topNPayload(widget) {
    if (!widget) return {};
    const type = normalizeWidgetType(widget.type);
    const style = widget.style || {};
    const topN = Number(style.topN) || 0;
    if (topN <= 0 || !['table', 'bar', 'hbar', 'line', 'area', 'combo', 'pie'].includes(type)) return {};
    const dir = style.sortDir === 'asc' ? 'asc' : 'desc';
    return {
        order_by: style.sortKey ? [{ field: style.sortKey, dir }] : [],
        limit: topN,
        ...(style.topNOthers ? { others: true } : {})
    };
}

/**
 * Payload de un widget para /api/engine/query(/batch).
 * Lo comparten el widget, el visor y el constructor de snapshots: el JSON tiene que
 * salir idéntico para que la caché de resultados acierte. `widget` (tipo y estilo)
 * aporta el Top N; los campos nuevos solo viajan si se usan, así los payloads de
 * reportes existentes no cambian.
 */
export function buildWidgetQueryPayload(query = {}, catalog = {}, filters = [], widget = null) {
    const grains = Object.fromEntries(
        Object.entries(query.dateGrains || {}).filter(([dim, grain]) => grain && (query.dimensions || []).includes(dim))
    );
    const kpi = normalizeWidgetType(widget?.type) === 'kpi' ? (query.kpi || {}) : {};
    const kpiEngine = kpi.dateField && (kpi.compare || kpi.sparkGrain)
        ? {
            date_field: kpi.dateField,
            compare: kpi.compare || '',
            compare_grain: kpi.compareGrain || 'month',
            spark_grain: kpi.sparkGrain || ''
        }
        : null;
    const metrics = kpi.targetMode === 'column' && kpi.targetColumn
        ? { ...(query.metrics || {}), [KPI_TARGET_KEY]: { agg: 'sum', column: kpi.targetColumn } }
        : (query.metrics || {});
    return {
        ...sourceRequestFields(query, catalog),
        dimensions: query.dimensions || [],
        metrics,
        row_calculated_fields: serializeCalculatedFields(query.row_calculated_fields),
        agg_calculated_fields: serializeCalculatedFields(query.agg_calculated_fields),
        filters: [...(filters || []), ...buildFixedEngineFilters(query.fixedFilters)],
        ...(Object.keys(grains).length ? { date_grains: grains } : {}),
        ...(kpiEngine ? { kpi: kpiEngine } : {}),
        ...topNPayload(widget)
    };
}

/** Normaliza el catálogo de fuentes/joins guardado en customConfig. */
export function normalizeDataSources(raw = {}) {
    if (Array.isArray(raw.dataSources) && raw.dataSources.length) {
        return {
            dataSources: raw.dataSources.map(item => ({
                id: item.id,
                kind: item.kind || (item.joinId ? 'join' : 'sheet'),
                label: item.label || item.sheet_name || 'Fuente',
                spreadsheet_id: item.spreadsheet_id || '',
                sheet_name: item.sheet_name || '',
                input: item.input || item.spreadsheet_id || '',
                joinId: item.joinId,
                filters: normalizeSourceFilters(item.filters),
                selectedColumns: normalizeSelectedColumns(item.selectedColumns || item.selected_columns),
                dateOrder: normalizeDateOrder(item.dateOrder || item.date_order),
            })),
            joins: (raw.joins || []).map(item => ({
                id: item.id,
                type: item.type || 'left',
                leftDataSourceId: item.leftDataSourceId || item.left_data_source_id || '',
                rightDataSourceId: item.rightDataSourceId || item.right_data_source_id || '',
                keys: (item.keys || []).length
                    ? item.keys.map(key => ({ left: key.left || '', right: key.right || '' }))
                    : [{ left: '', right: '' }]
            }))
        };
    }
    const src = raw.dataSource || firstPageDataSource(raw);
    if (!src.spreadsheet_id) return { dataSources: [], joins: [] };
    const id = 'ds-legacy';
    return {
        dataSources: [createDataSource({
            id,
            label: src.sheet_name || 'Fuente',
            spreadsheet_id: src.spreadsheet_id,
            sheet_name: src.sheet_name,
            input: src.spreadsheet_id
        })],
        joins: []
    };
}

export function pruneJoinGraph(dataSources = [], joins = [], removedSourceIds = []) {
    const removed = new Set(removedSourceIds.filter(Boolean));
    let nextSources = dataSources.slice();
    let nextJoins = joins.slice();
    let changed = true;
    while (changed) {
        changed = false;
        const dropJoinIds = nextJoins
            .filter(join => removed.has(join.leftDataSourceId) || removed.has(join.rightDataSourceId))
            .map(join => join.id);
        if (dropJoinIds.length) {
            changed = true;
            const dropSet = new Set(dropJoinIds);
            nextJoins = nextJoins.filter(join => !dropSet.has(join.id));
            nextSources.forEach((source) => {
                if (source.kind === 'join' && dropSet.has(source.joinId)) removed.add(source.id);
            });
            nextSources = nextSources.filter(source => !(source.kind === 'join' && dropSet.has(source.joinId)));
        }
        const before = nextSources.length;
        nextSources = nextSources.filter(source => !removed.has(source.id));
        if (nextSources.length !== before) changed = true;
        nextSources.forEach((source) => {
            if (source.kind === 'join' && !nextJoins.some(join => join.id === source.joinId)) {
                removed.add(source.id);
                changed = true;
            }
        });
        nextSources = nextSources.filter(source => !removed.has(source.id));
    }
    return { dataSources: nextSources, joins: nextJoins, removedIds: removed };
}

export function remapWidgetsDataSource(widgets = [], removedIds, fallback = {}) {
    const gone = removedIds instanceof Set ? removedIds : new Set(removedIds || []);
    if (!gone.size) return widgets;
    return widgets.map((widget) => {
        let next = widget;
        if (next.query?.dataSourceId && gone.has(next.query.dataSourceId)) {
            next = {
                ...next,
                query: {
                    ...next.query,
                    dataSourceId: fallback.id || fallback.dataSourceId || '',
                    spreadsheet_id: fallback.spreadsheet_id ?? next.query.spreadsheet_id,
                    sheet_name: fallback.sheet_name ?? next.query.sheet_name
                }
            };
        }
        if (next.children?.widgets) {
            next = {
                ...next,
                children: {
                    ...next.children,
                    widgets: remapWidgetsDataSource(next.children.widgets, gone, fallback)
                }
            };
        }
        return next;
    });
}

export function joinKeyNamesForSource(sourceId, joins = []) {
    const names = [];
    (joins || []).forEach((join) => {
        (join.keys || []).forEach((key) => {
            if (join.leftDataSourceId === sourceId && key.left) names.push(key.left);
            if (join.rightDataSourceId === sourceId && key.right) names.push(key.right);
        });
    });
    return [...new Set(names)];
}

export function applySourceIdToWidgets(widgets = [], sourceId) {
    if (!sourceId) return widgets;
    return widgets.map(widget => {
        const next = { ...widget };
        if (next.query && !next.query.dataSourceId) {
            next.query = { ...next.query, dataSourceId: sourceId };
        }
        if (next.children?.widgets) {
            next.children = {
                ...next.children,
                widgets: applySourceIdToWidgets(next.children.widgets, sourceId)
            };
        }
        return next;
    });
}

export function sourceLabel(source) {
    if (!source) return 'Fuente';
    if (source.kind === 'join') return source.label || 'Unión';
    return source.label || source.sheet_name || 'Fuente';
}

export const AGG_OPTIONS = [
    { value: 'sum', label: 'Suma' },
    { value: 'mean', label: 'Promedio' },
    { value: 'median', label: 'Mediana' },
    { value: 'count', label: 'Conteo' },
    { value: 'count_distinct', label: 'Conteo único' },
    { value: 'max', label: 'Máximo' },
    { value: 'min', label: 'Mínimo' }
];

/** Cómo leer fechas ambiguas (05/03/2026) de una fuente; el motor decide en «auto». */
export const DATE_ORDER_OPTIONS = [
    { value: 'auto', label: 'Automático' },
    { value: 'dmy', label: 'Día/Mes/Año' },
    { value: 'mdy', label: 'Mes/Día/Año' }
];

export function normalizeDateOrder(value) {
    return ['dmy', 'mdy'].includes(value) ? value : 'auto';
}

// «Automático» usa el total que calcula el motor con la agregación de la métrica
// (promedio, conteo único, mediana…), no la suma de las filas visibles.
export const TOTAL_AGG_OPTIONS = [
    { value: 'auto', label: 'Automático' },
    { value: 'sum', label: 'Suma' },
    { value: 'mean', label: 'Promedio' },
    { value: 'max', label: 'Máximo' },
    { value: 'min', label: 'Mínimo' },
    { value: 'formula', label: 'Recalcular fórmula' },
    { value: 'hide', label: 'Ocultar' }
];

export const INNER_AGG_OPTIONS = [
    { value: '', label: 'Valores' },
    { value: 'sum', label: 'Totales' },
    { value: 'max', label: 'Máximos' },
    { value: 'min', label: 'Mínimos' },
    { value: 'mean', label: 'Promedios' },
    { value: 'count', label: 'Conteos' }
];

export function parseMetricSpec(spec) {
    if (spec && typeof spec === 'object') {
        return {
            agg: spec.agg || spec.outer || 'sum',
            innerAgg: spec.inner_agg || spec.innerAgg || '',
            innerBy: spec.inner_by || spec.innerBy || ''
        };
    }
    const raw = String(spec || 'sum');
    if (raw.includes('_of_')) {
        const [agg, innerAgg] = raw.split('_of_');
        return { agg: agg || 'sum', innerAgg: innerAgg || '', innerBy: '' };
    }
    return { agg: raw || 'sum', innerAgg: '', innerBy: '' };
}

export function describeMetricSpec(spec) {
    const parsed = parseMetricSpec(spec);
    const outer = AGG_OPTIONS.find(opt => opt.value === parsed.agg)?.label || 'Suma';
    if (!parsed.innerAgg) return `${outer} de valores`;
    const inner = INNER_AGG_OPTIONS.find(opt => opt.value === parsed.innerAgg)?.label || parsed.innerAgg;
    if (!parsed.innerBy) return `${outer} de ${String(inner).toLowerCase()}`;
    return `${outer} de ${String(inner).toLowerCase()} · primero por ${parsed.innerBy}`;
}

export function serializeMetricSpec({ agg = 'sum', innerAgg = '', innerBy = '' } = {}) {
    if (!innerAgg) return agg;
    const spec = { agg, inner_agg: innerAgg };
    if (innerBy) spec.inner_by = innerBy;
    return spec;
}

export const DEFAULT_STYLE = {
    color: CHART_PALETTE[0],
    showLegend: true,
    showGrid: true,
    showLabels: false,
    labelFormat: 'value',
    stacked: false,
    innerRadius: 0,
    numberFormat: 'default',
    numberDecimals: null,
    barRadius: 4,
    strokeWidth: 2,
    showDots: true,
    fillArea: false,
    kpiPrefix: '',
    kpiSuffix: '',
    kpiFontSize: 28,
    striped: true,
    showTotals: true,
    tableDensity: 'comfortable',
    freezeFirstColumn: true,
    showTitle: true,
    showExport: true,
    showHeader: true,
    seriesColors: {},
    legendPosition: 'bottom',
    xAxisLabel: '',
    yAxisLabel: '',
    showCategoryAxis: true,
    showValueAxis: true,
    showCategoryTicks: true,
    showValueTicks: true,
    valueAxisFormat: 'inherit',
    valueAxisDecimals: null,
    valueAxisMin: '',
    valueAxisMax: '',
    categoryTickAngle: 0,
    categoryTickSize: 12,
    valueTickSize: 12,
    categoryTickMaxChars: 18,
    categoryTickInterval: 'auto',
    topN: 0,
    sortKey: '',
    sortDir: 'desc',
    tablePaginate: true,
    tableWidthMode: 'fit',
    comboLineMetrics: [],
    cardBackground: '#ffffff',
    cardRadius: 10,
    mergeRepeatedCells: true,
    headerWrap: true,
    headerFontSize: 13,
    headerColor: '#ffffff',
    headerBackground: '#5E35B1',
    headerFontWeight: 600,
    headerAlign: 'auto',
    headerItalic: false,
    bodyFontSize: 13,
    bodyColor: '#202124',
    bodyBackground: '#ffffff',
    stripeBackground: '#f6f2fb',
    dimTextColor: '#4a148c',
    bodyFontWeight: 400,
    bodyAlign: 'auto',
    totalsFontSize: 13,
    totalsColor: '#4a148c',
    totalsBackground: '#ede7f6',
    totalsFontWeight: 700,
    totalsItalic: false,
    totalsAlign: 'auto',
    tableFontFamily: 'inherit'
};

export const TEXT_FONT_OPTIONS = [
    { value: 'inherit', label: 'Del reporte' },
    { value: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif', label: 'Segoe UI' },
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
    { value: '"Trebuchet MS", Helvetica, sans-serif', label: 'Trebuchet' },
    { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
    { value: '"Courier New", Courier, monospace', label: 'Courier' }
];

export const TEXT_STYLE_DEFAULTS = {
    color: '#202124',
    showTitle: false,
    fontSize: 16,
    align: 'left',
    valign: 'top',
    fontWeight: 400,
    italic: false,
    underline: false,
    fontFamily: 'inherit',
    lineHeight: 1.45,
    letterSpacing: 0,
    textTransform: 'none',
    textPadding: 8,
    textPaddingX: null,
    textPaddingY: null,
    textBorderWidth: 0,
    textBorderColor: '#dadce0'
};

const TEXT_VALIGN = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };

function resolveTextBoxPadding(style = {}) {
    const fallbackRaw = style.textPadding;
    const fallback = fallbackRaw == null || fallbackRaw === '' ? 8 : Number(fallbackRaw);
    const base = Number.isFinite(fallback) ? Math.max(0, fallback) : 8;
    const xRaw = style.textPaddingX;
    const yRaw = style.textPaddingY;
    const x = xRaw == null || xRaw === '' ? base : Number(xRaw);
    const y = yRaw == null || yRaw === '' ? base : Number(yRaw);
    return {
        x: Number.isFinite(x) ? Math.max(0, x) : 0,
        y: Number.isFinite(y) ? Math.max(0, y) : 0,
    };
}

export function getTextContentStyle(style = {}, { editor = false } = {}) {
    const s = { ...TEXT_STYLE_DEFAULTS, ...style };
    const { x: padX, y: padY } = resolveTextBoxPadding(s);
    const borderW = Number(s.textBorderWidth) || 0;
    const base = {
        color: s.color || '#202124',
        fontSize: `${s.fontSize || 16}px`,
        textAlign: s.align || 'left',
        fontWeight: s.fontWeight || 400,
        fontStyle: s.italic ? 'italic' : 'normal',
        textDecoration: s.underline ? 'underline' : 'none',
        fontFamily: !s.fontFamily || s.fontFamily === 'inherit' ? 'inherit' : s.fontFamily,
        lineHeight: s.lineHeight || 1.45,
        letterSpacing: `${Number(s.letterSpacing) || 0}px`,
        textTransform: s.textTransform || 'none',
        paddingTop: padY,
        paddingRight: padX,
        paddingBottom: padY,
        paddingLeft: padX,
        margin: 0,
        boxSizing: 'border-box',
        borderWidth: borderW > 0 ? borderW : 0,
        borderStyle: borderW > 0 ? 'solid' : 'none',
        borderColor: s.textBorderColor || '#dadce0',
        borderRadius: borderW > 0 ? 6 : 0
    };
    if (editor) {
        return {
            ...base,
            width: '100%',
            flex: 1,
            minHeight: 0,
            resize: 'none',
            outline: 'none',
            backgroundColor: 'transparent'
        };
    }
    return {
        ...base,
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: TEXT_VALIGN[s.valign] || 'flex-start'
    };
}

export function getWidgetStyle(widget) {
    const type = normalizeWidgetType(widget?.type);
    const isText = type === 'text';
    const isBlock = type === 'container';
    const merged = {
        ...DEFAULT_STYLE,
        ...(isText ? TEXT_STYLE_DEFAULTS : {}),
        ...(isBlock ? {
            innerPadding: 0,
            innerGap: 0,
            showChildHeaders: true,
            titleFontSize: 16,
            titleColor: '#4a148c',
            titleAlign: 'left',
            titleFontWeight: 700,
            titleItalic: false,
            titleFontFamily: 'inherit',
            subtitle: '',
            subtitleFontSize: 12,
            subtitleColor: '#5f6368',
            cardPadding: 0,
            cardMargin: 0,
            borderWidth: 0,
            borderColor: '#7e57c2'
        } : {}),
        ...(type === 'table' ? {
            headerFontSize: 13,
            headerColor: '#ffffff',
            headerBackground: '#5E35B1',
            headerFontWeight: 600,
            headerAlign: 'auto',
            bodyFontSize: 13,
            bodyColor: '#202124',
            bodyBackground: '#ffffff',
            stripeBackground: '#f6f2fb',
            dimTextColor: '#4a148c',
            totalsFontSize: 13,
            totalsColor: '#4a148c',
            totalsBackground: '#ede7f6',
            totalsFontWeight: 700,
            tableFontFamily: 'inherit'
        } : {}),
        ...(isText ? {} : { color: widget?.color || DEFAULT_STYLE.color }),
        ...(widget?.style || {})
    };
    if (isText && merged.color === CHART_PALETTE[0]) merged.color = '#202124';
    return merged;
}

export function getBlockTitleStyle(style = {}) {
    return {
        margin: 0,
        fontSize: `${style.titleFontSize || 16}px`,
        color: style.titleColor || '#4a148c',
        textAlign: style.titleAlign || 'left',
        fontWeight: style.titleFontWeight || 700,
        fontStyle: style.titleItalic ? 'italic' : 'normal',
        fontFamily: style.titleFontFamily && style.titleFontFamily !== 'inherit' ? style.titleFontFamily : 'inherit',
        lineHeight: 1.25,
        flexShrink: 0,
        minWidth: 0
    };
}

export function getBlockSubtitleStyle(style = {}) {
    return {
        margin: 0,
        fontSize: `${style.subtitleFontSize || 12}px`,
        color: style.subtitleColor || '#5f6368',
        textAlign: style.titleAlign || 'left',
        fontFamily: style.titleFontFamily && style.titleFontFamily !== 'inherit' ? style.titleFontFamily : 'inherit',
        lineHeight: 1.35
    };
}

export function getBlockFrameStyle(style = {}) {
    const pad = Number(style.cardPadding);
    const margin = Number(style.cardMargin);
    const borderW = Number(style.borderWidth) || 0;
    return {
        padding: Number.isFinite(pad) ? pad : 0,
        margin: Number.isFinite(margin) ? margin : 0,
        border: borderW > 0 ? `${borderW}px solid ${style.borderColor || '#7e57c2'}` : undefined,
        boxSizing: 'border-box'
    };
}

export function parseMetricFormat(raw, fallbackType = 'default') {
    let type = fallbackType;
    let decimals = null;
    if (raw && typeof raw === 'object') {
        type = raw.type && raw.type !== 'inherit' ? raw.type : fallbackType;
        const n = raw.decimals;
        decimals = n === null || n === undefined || n === '' ? null : Number(n);
    } else if (typeof raw === 'string' && raw && raw !== 'inherit') {
        type = raw;
    }
    if (decimals != null && !Number.isFinite(decimals)) decimals = null;
    if (decimals != null) decimals = Math.max(0, Math.min(8, Math.round(decimals)));
    return { type: type || fallbackType, decimals };
}

function fractionOpts(decimals, fallbackMax, fallbackMin) {
    if (decimals == null) {
        return fallbackMin == null
            ? { maximumFractionDigits: fallbackMax }
            : { minimumFractionDigits: fallbackMin, maximumFractionDigits: fallbackMax };
    }
    return { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
}

export function formatNumber(val, numberFormat = 'default', decimals = null) {
    if (val === null || val === undefined) return 'N/A';
    if (typeof val !== 'number') return String(val);
    const frac = decimals == null || Number.isNaN(Number(decimals)) ? null : Number(decimals);
    if (numberFormat === 'percent') {
        return `${(val * 100).toLocaleString(undefined, fractionOpts(frac, 1, frac == null ? 0 : frac))}%`;
    }
    if (numberFormat === 'compact') {
        return new Intl.NumberFormat(undefined, {
            notation: 'compact',
            maximumFractionDigits: frac == null ? 1 : frac
        }).format(val);
    }
    if (numberFormat === 'integer') {
        const digits = frac == null ? 0 : frac;
        return val.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
    if (numberFormat === 'decimal') {
        return val.toLocaleString(undefined, fractionOpts(frac, 4, 2));
    }
    if (numberFormat === 'currency_mxn' || numberFormat === 'currency') {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            ...fractionOpts(frac, 2, 2)
        }).format(val);
    }
    if (numberFormat === 'currency_usd') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            ...fractionOpts(frac, 2, 2)
        }).format(val);
    }
    return val.toLocaleString(undefined, fractionOpts(frac, 2));
}

export function metricFormatOf(query, key, fallback = 'default') {
    return parseMetricFormat(query?.metricFormats?.[key], fallback).type;
}

export function normalizeWidgetType(type) {
    const t = String(type || 'table').toLowerCase();
    if (t === 'barchart' || t === 'chart') return 'bar';
    if (t === 'linechart') return 'line';
    if (t === 'piechart') return 'pie';
    if (t === 'scorecard' || t === 'card') return 'kpi';
    return t;
}

export function createWidget(type, dataSource) {
    const preset = WIDGET_TYPES.find(item => item.type === type) || WIDGET_TYPES[0];
    const id = `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    return {
        id,
        type: preset.type,
        title: preset.label,
        color: type === 'text' ? '#202124' : CHART_PALETTE[0],
        style: {
            ...DEFAULT_STYLE,
            ...(type === 'text' ? TEXT_STYLE_DEFAULTS : {}),
            ...(type === 'image' ? {
                showTitle: false,
                objectFit: 'contain',
                cardBackground: 'transparent',
                cardPadding: 0
            } : {}),
            ...(type === 'container' ? {
                innerPadding: 0,
                innerGap: 0,
                showChildHeaders: true,
                titleFontSize: 16,
                titleColor: '#4a148c',
                titleAlign: 'left',
                titleFontWeight: 700,
                titleItalic: false,
                titleFontFamily: 'inherit',
                subtitle: '',
                subtitleFontSize: 12,
                subtitleColor: '#5f6368',
                cardPadding: 0,
                cardMargin: 0,
                borderWidth: 0,
                borderColor: '#7e57c2'
            } : {})
        },
        filterId: type === 'slicer' ? `slicer-${id}` : undefined,
        content: type === 'text' ? 'Escribe el texto del dashboard…' : '',
        query: {
            dataSourceId: dataSource.id || dataSource.dataSourceId || '',
            spreadsheet_id: dataSource.spreadsheet_id,
            sheet_name: dataSource.sheet_name,
            dimensions: [],
            metrics: {},
            row_calculated_fields: [],
            agg_calculated_fields: [],
            visibleMetrics: [],
            aliases: {},
            metricFormats: {},
            totalAggs: {},
            totalFormulas: {},
            conditionalFormats: {},
            fixedFilters: []
        },
        children: type === 'container' ? { widgets: [], layout: [] } : undefined
    };
}

export function isContainerType(type) {
    return normalizeWidgetType(type) === 'container';
}

/** Dentro de un bloque, estos tipos se pegan arriba en lugar de estirar/centrar. */
export function hugsBlockTop(type) {
    const t = normalizeWidgetType(type);
    return t === 'text' || t === 'kpi' || t === 'slicer' || t === 'image' || t === 'divider';
}

export function isChartType(type) {
    return ['bar', 'hbar', 'line', 'area', 'combo', 'pie'].includes(normalizeWidgetType(type));
}

export function findFreeSlot(existingLayout = [], w, h, columns = 8) {
    const cols = Math.max(1, Number(columns) || 8);
    const width = Math.max(1, Math.min(cols, w));
    const height = Math.max(1, h);
    const occupied = (existingLayout || []).map(item => ({
        x: item.x || 0,
        y: item.y || 0,
        w: item.w || 1,
        h: item.h || 1
    }));
    const collides = (x, y) => occupied.some(o =>
        x < o.x + o.w && x + width > o.x && y < o.y + o.h && y + height > o.y
    );
    const maxY = occupied.reduce((acc, o) => Math.max(acc, o.y + o.h), 0);
    for (let y = 0; y <= maxY + 1; y += 1) {
        for (let x = 0; x <= cols - width; x += 1) {
            if (!collides(x, y)) return { x, y };
        }
    }
    return { x: 0, y: maxY };
}

export function findSlotNear(existingLayout = [], w, h, columns = 8, preferX = 0, preferY = 0) {
    const cols = Math.max(1, Number(columns) || 8);
    const width = Math.max(1, Math.min(cols, w));
    const height = Math.max(1, h);
    const occupied = (existingLayout || []).map(item => ({
        x: item.x || 0,
        y: item.y || 0,
        w: item.w || 1,
        h: item.h || 1
    }));
    const collides = (x, y) => occupied.some(o =>
        x < o.x + o.w && x + width > o.x && y < o.y + o.h && y + height > o.y
    );
    const x0 = Math.max(0, Math.min(cols - width, Number(preferX) || 0));
    const y0 = Math.max(0, Number(preferY) || 0);
    if (!collides(x0, y0)) return { x: x0, y: y0 };
    const maxY = occupied.reduce((acc, o) => Math.max(acc, o.y + o.h), y0 + height);
    for (let y = y0; y <= maxY + 1; y += 1) {
        for (let x = 0; x <= cols - width; x += 1) {
            if (!collides(x, y)) return { x, y };
        }
    }
    return findFreeSlot(existingLayout, width, height, cols);
}

export function createLayoutItem(widget, existingLayout = [], columns = 8, options = {}) {
    const preset = WIDGET_TYPES.find(item => item.type === widget.type) || WIDGET_TYPES[0];
    const cols = Math.max(1, Number(columns) || 8);
    const inner = Boolean(options.inner);
    let scaled = widget.type === 'divider'
        ? cols
        : Math.max(1, Math.round((preset.w || 4) * (cols / 12)));
    if (isContainerType(widget.type) && !inner) {
        scaled = Math.max(3, Math.round(cols * 0.5));
    }
    if (inner && widget.type === 'text') {
        scaled = cols;
    } else if (inner && !isContainerType(widget.type) && widget.type !== 'divider') {
        scaled = Math.min(scaled, Math.max(2, Math.ceil(cols / 2)));
    }
    const w = Math.min(cols, scaled);
    const fine = cols >= 24;
    let h = Math.max(1, Math.round((preset.h || 2) * (fine ? 2 : 1)));
    if (inner && !isContainerType(widget.type)) {
        if (widget.type === 'text') h = Math.max(fine ? 6 : 3, h);
        else if (widget.type === 'divider') h = fine ? 2 : 1;
        else if (widget.type === 'kpi') h = Math.min(h, fine ? 6 : 3);
        else h = Math.min(h, fine ? 8 : 4);
    } else if (isContainerType(widget.type)) {
        h = Math.max(fine ? 12 : 6, h);
    }
    const slot = findFreeSlot(existingLayout, w, h, cols);
    return {
        i: widget.id,
        x: slot.x,
        y: slot.y,
        w,
        h,
        minW: 1,
        minH: 1,
        maxW: cols
    };
}

export function layoutBottom(items = []) {
    return (items || []).reduce((acc, item) => Math.max(acc, (item.y || 0) + (item.h || 1)), 0);
}

export function reflowLayout(items = [], columns = INNER_CANVAS_COLUMNS) {
    const cols = Math.max(1, Number(columns) || INNER_CANVAS_COLUMNS);
    const ordered = [...(items || [])].sort((a, b) => ((a.y || 0) - (b.y || 0)) || ((a.x || 0) - (b.x || 0)));
    const placed = [];
    ordered.forEach(item => {
        const w = Math.max(1, Math.min(cols, item.w || 1));
        const h = Math.max(1, item.h || 1);
        const slot = findFreeSlot(placed, w, h, cols);
        placed.push({
            ...item,
            x: slot.x,
            y: slot.y,
            w,
            h,
            maxW: cols,
            minW: 1,
            minH: 1
        });
    });
    return placed;
}

export function containerHeightForInner(innerLayout, parentRowHeight = 48) {
    const innerRow = Math.max(12, Number(parentRowHeight) || CANVAS_ROW_HEIGHT);
    const chrome = 64;
    const px = layoutBottom(innerLayout) * innerRow + chrome;
    return Math.max(fineHeight(parentRowHeight), Math.ceil(px / Math.max(12, Number(parentRowHeight) || CANVAS_ROW_HEIGHT)));
}

function fineHeight(parentRowHeight) {
    return (Number(parentRowHeight) || CANVAS_ROW_HEIGHT) <= 24 ? 12 : 6;
}

export function cloneWidget(widget) {
    const copy = JSON.parse(JSON.stringify(widget || {}));
    const remap = (node) => {
        const id = `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        node.id = id;
        node.groupId = undefined;
        node.filterId = node.type === 'slicer' ? `slicer-${id}` : undefined;
        if (node.children?.widgets?.length) {
            const idMap = {};
            node.children.widgets = node.children.widgets.map((child) => {
                const oldId = child.id;
                const next = remap(child);
                idMap[oldId] = next.id;
                return next;
            });
            node.children.layout = (node.children.layout || []).map(item => ({
                ...item,
                i: idMap[item.i] || item.i
            }));
        }
        return node;
    };
    const result = remap(copy);
    result.title = `${widget?.title || 'Widget'} (copia)`;
    return result;
}

export function cloneReportPage(page, name) {
    const widgets = (page?.widgets || []).map(widget => {
        const next = cloneWidget(widget);
        next.title = widget.title;
        return next;
    });
    const idMap = {};
    (page?.widgets || []).forEach((widget, index) => {
        idMap[widget.id] = widgets[index].id;
    });

    // Los slicers ya traen su propio filterId nuevo (cloneWidget lo regenera); hay que
    // re-vincular los filtros de la hoja a esos ids nuevos para que sigan controlando su widget.
    const filterIdMap = {};
    const mapFilterIds = (oldList, newList) => {
        (oldList || []).forEach((oldWidget, index) => {
            const newWidget = (newList || [])[index];
            if (oldWidget?.filterId && newWidget?.filterId) {
                filterIdMap[oldWidget.filterId] = newWidget.filterId;
            }
            if (oldWidget?.children?.widgets) {
                mapFilterIds(oldWidget.children.widgets, newWidget?.children?.widgets);
            }
        });
    };
    mapFilterIds(page?.widgets, widgets);

    const filters = (page?.filters || []).reduce((acc, def, index) => {
        if (String(def.id).startsWith('slicer-')) {
            const newId = filterIdMap[def.id];
            if (newId) acc.push({ ...def, id: newId });
            return acc;
        }
        // Filtro manual del informe: id nuevo para que sus valores no queden
        // compartidos con la hoja original (filterValues es un mapa global por id).
        acc.push({ ...def, id: `filter-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 5)}` });
        return acc;
    }, []);

    return createReportPage(name || `${page?.name || 'Hoja'} (copia)`, {
        widgets,
        layout: (page?.layout || []).map(item => ({ ...item, i: idMap[item.i] || item.i })),
        canvas: { ...DEFAULT_CANVAS, ...(page?.canvas || {}) },
        icon: page?.icon || 'page',
        accent: page?.accent || '',
        hidden: Boolean(page?.hidden),
        sectionId: page?.sectionId || null,
        filters
    });
}

export function findWidgetById(list, id) {
    for (const widget of list || []) {
        if (widget.id === id) return widget;
        const nested = findWidgetById(widget.children?.widgets, id);
        if (nested) return nested;
    }
    return null;
}

export function findParentWidget(list, id) {
    for (const widget of list || []) {
        if ((widget.children?.widgets || []).some(child => child.id === id)) return widget;
        const nested = findParentWidget(widget.children?.widgets, id);
        if (nested) return nested;
    }
    return null;
}

export function findLayoutItem(list, layout, id) {
    const root = (layout || []).find(item => item.i === id);
    if (root) return root;
    for (const widget of list || []) {
        const inner = (widget.children?.layout || []).find(item => item.i === id);
        if (inner) return inner;
        const nested = findLayoutItem(widget.children?.widgets, null, id);
        if (nested) return nested;
    }
    return null;
}

export function updateWidgetById(list, id, updater) {
    return (list || []).map(widget => {
        if (widget.id === id) return updater(widget);
        if (widget.children?.widgets) {
            return {
                ...widget,
                children: {
                    ...widget.children,
                    widgets: updateWidgetById(widget.children.widgets, id, updater)
                }
            };
        }
        return widget;
    });
}

export function removeWidgetById(list, id) {
    return (list || []).reduce((acc, widget) => {
        if (widget.id === id) return acc;
        if (widget.children?.widgets) {
            acc.push({
                ...widget,
                children: {
                    ...widget.children,
                    widgets: removeWidgetById(widget.children.widgets, id),
                    layout: (widget.children.layout || []).filter(item => item.i !== id)
                }
            });
            return acc;
        }
        acc.push(widget);
        return acc;
    }, []);
}

export function collectFilterIds(widget) {
    const ids = [];
    if (widget?.filterId) ids.push(widget.filterId);
    (widget?.children?.widgets || []).forEach(child => ids.push(...collectFilterIds(child)));
    return ids;
}

export function addWidgetToContainer(container, widget, columns = INNER_CANVAS_COLUMNS) {
    const layout = [...(container.children?.layout || [])];
    const nextWidget = {
        ...widget,
        style: {
            ...(widget.style || {}),
            valign: widget.style?.valign || 'top'
        }
    };
    const nextItem = createLayoutItem(nextWidget, layout, columns, { inner: true });
    return {
        ...container,
        children: {
            widgets: [...(container.children?.widgets || []), nextWidget],
            layout: reflowLayout([...layout, nextItem], columns)
        }
    };
}

export function stampDataSource(widget, dataSource) {
    const next = {
        ...widget,
        query: widget.query
            ? {
                ...widget.query,
                dataSourceId: dataSource.id || dataSource.dataSourceId || widget.query.dataSourceId,
                spreadsheet_id: dataSource.spreadsheet_id ?? widget.query.spreadsheet_id,
                sheet_name: dataSource.sheet_name ?? widget.query.sheet_name
            }
            : widget.query
    };
    if (next.children?.widgets) {
        next.children = {
            ...next.children,
            widgets: next.children.widgets.map(child => stampDataSource(child, dataSource))
        };
    }
    return next;
}
